import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createProject, deleteProject, getProject, getProjects, updateProject } from "./requests";
import type { CustomQueryOptions, Filter, Pagination, Project, ProjectListItem } from "../types";

// Type for creating a project (without auto-generated fields)
type CreateProjectData = {
  title: string;
  summary: string;
  timeline: string;
  tags: string[];
  media?: {
    name: string;
    url: string;
  };
  organisationId: string;
  collaboratingOrganisations: string[];
  status: 'pending' | 'completed';
  continent: string;
  country: string;
  region: string;
  state: string;
  keyOutcomes: string;
};

export const useCreateProject = (onSuccess: () => void, onError: (error: any) => void) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateProjectData) => createProject(data as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'], exact: false });
      onSuccess();
    },
    onError
  });
};

export const useGetProjects = (filter: Filter, options: CustomQueryOptions<{data: ProjectListItem[], pagination: Pagination}>) => {
  return useQuery({
    queryKey: ['projects', `all-${JSON.stringify(filter)}`],
    queryFn: () => getProjects(filter),
    ...options
  });
};

export const useGetProject = (id: string, options: CustomQueryOptions<Project>) => {
  return useQuery({
    queryKey: ['projects', id],
    queryFn: () => getProject(id),
    ...options
  });
};

export const useUpdateProject = (onSuccess: () => void, onError: (error: any) => void) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({id, data}: {id: string, data: Omit<CreateProjectData, 'organisationId'>}) => updateProject(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'], exact: false });
      onSuccess();
    },
    onError
  });
};

export const useDeleteProject = (onSuccess: () => void, onError: (error: any) => void) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteProject(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'], exact: false });
      onSuccess();
    },
    onError
  });
};